import { useTranslation } from 'react-i18next'
import './styles.scss'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { InputText } from 'primereact/inputtext'
import { ROUTE_STRINGS } from 'Utils/Constants'
import { getLocalAppSettings, getLocalFreeBusinessGeneralMenus, getLocalLoggedInStatus, getLocalUserDetails } from 'Utils/LocalStorage_Handler'
import { commonMethods } from 'Utils/commonMethods'
import UserGuide from 'Screens/UserGuide'
import Subscription from 'Screens/Subscription'
import SupportRequest from 'Screens/SupportRequest'
import AboutUs from 'Screens/AboutUs'
import apiAdapterCoffeeWeb from 'Services/apiAdapter_CoffeeWeb'
import { setUser } from '@sentry/react'
import CoffeeGames from 'Screens/CoffeeGames'
import Button from 'Components/UIComponent/Button'
import CoffeeCafes from 'Screens/CoffeeCafes'
import { CoffeeWebContext } from 'Context/CoffeeWebContext'
import CoffeeCommunities from './Components/CoffeeCommunities/index'
import CoffeeCast from './Components/CoffeeCast'
import CoffeeCart from './Components/CoffeeCart'
import CoffeePostScreen from './Components/CoffeePost'

// --- Utility Functions ---

const normalizeUrl = (url) => url?.replace(/^\//, '')?.toLowerCase()

const validityFormatter = (date, dateFormatBasedOnUser) => {
  const [validityString, validTill] = date.split(':').map((el) => el.trim())

  return `${validityString}: ${dateFormatBasedOnUser(validTill, 'spaceFormat')}`
}

function generateLinearGradientColor(userDetails, realtimeSubscriptionData) {
  const defaultColor = '#50b3f6'
  const color = realtimeSubscriptionData?.subscriptionDetails?.SubscriptionColor || userDetails?.sub?.subscriptionColor || defaultColor

  const colorPart1 = color.slice(0, 7)
  const colorPart2 = color.slice(9, 16)
  const colorPart3 = color.slice(18, 27)

  return `linear-gradient(100deg, ${colorPart1}, ${colorPart2}, ${colorPart3})`
}

const CoffeeWebSocial = () => {
  const history = useHistory()
  const { i18n, t } = useTranslation()
  const location = useLocation()
  const { dateFormatBasedOnUser } = commonMethods
  const params = new URLSearchParams(window.location.search)
  const view = params.get('view')
  const tab = params.get('tab')
  const hideViews = [ROUTE_STRINGS.coffeePost]
  const [searchTextField, setSearchTextField] = useState('')
  const containerRef = useRef(null)
  const { realtimeSubscriptionData } = useContext(CoffeeWebContext)

  const userLoginStatus = getLocalLoggedInStatus()
  const userDetails = getLocalUserDetails()
  const appSettings = getLocalAppSettings()
  const locationRef = useRef()

  const [active, setActive] = useState('')
  const [isTrialUser, setIsTrialUser] = useState(userDetails?.isTrialUser)
  const [menuItems, setMenuItems] = useState([])
  const [coffeewebParentItems, setCoffeewebParentItems] = useState([]) // Items with coffeeweb as parentId (for right content only)
  const [showAboutUsNavLogo, setShowAboutUsNavLogo] = useState(true)
  const [userGeolocation, setUserGeolocation] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [organizationStatus, setOrganizationStatus] = useState(null)

  const previousViewRef = useRef(null)
  const realtimeSubscriptionName = (realtimeSubscriptionData?.subscriptionDetails?.SubscriptionName || '').trim()
  const displayedSubscriptionName = realtimeSubscriptionName || userDetails?.sub?.subTypeName
  const displayedRenewActionText = userDetails?.sub?.renewProcess || t('RENEW')
  const shouldShowRenewAction = appSettings?.showrenewal && (Boolean(realtimeSubscriptionName) || Boolean(userDetails?.sub?.renewProcess))

  const freeMenusToDisplay = tab === 'general' ? getLocalFreeBusinessGeneralMenus()?.generalMenu : getLocalFreeBusinessGeneralMenus()?.businessMenu

  // Handle view param on initial load and when it changes
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const view = params.get('view')
    const prev = params.get('prev')
    const tab = params.get('tab')

    // Immediately hide icon if view is about-coffeeweb (handles refresh case)
    if (view && normalizeUrl(view) === 'about-coffeeweb') {
      setShowAboutUsNavLogo(false)
    }

    if (menuItems?.length > 0) {
      const normalizedMenuUrls = menuItems.map((m) => normalizeUrl(m.menuUrl))
      const normalizedParentItems = coffeewebParentItems.map((m) => normalizeUrl(m.menuUrl))
      const allowedViews = [...new Set([...normalizedMenuUrls, ...normalizedParentItems, 'about-coffeeweb'])]

      if (view && allowedViews.includes(view.toLowerCase())) {
        const normalizedView = view.toLowerCase()

        setActive(normalizedView)

        // Store the previous view before setting about-coffeeweb
        if (normalizedView === 'about-coffeeweb') {
          // If we're on about-coffeeweb, hide the icon
          setShowAboutUsNavLogo(false)
          // Use prev param from URL if available (handles refresh), otherwise use stored ref or first menu item
          const normalizedPrev = prev ? normalizeUrl(prev) : null

          if (normalizedPrev && allowedViews.includes(normalizedPrev)) {
            previousViewRef.current = normalizedPrev
            locationRef.current = previousViewRef.current
          } else if (!previousViewRef.current && menuItems.length > 0) {
            previousViewRef.current = normalizeUrl(menuItems[0]?.menuUrl)
            locationRef.current = previousViewRef.current
          } else {
            locationRef.current = previousViewRef.current || normalizeUrl(menuItems[0]?.menuUrl)
          }
        } else {
          // If we're on a regular menu item, show the icon and store it as previous
          setShowAboutUsNavLogo(true)
          previousViewRef.current = normalizedView
          locationRef.current = normalizedView
        }
      } else {
        const defaultMenuUrl = ROUTE_STRINGS.coffeePost

        locationRef.current = defaultMenuUrl
        previousViewRef.current = defaultMenuUrl

        setActive(defaultMenuUrl)
        const newParams = new URLSearchParams(location.search)

        newParams.set('view', defaultMenuUrl)
        history.replace(`${location.pathname}?${newParams.toString()}`)
      }
    }
  }, [menuItems, coffeewebParentItems, location.search])

  // useEffect(() => {
  //   const submenuItems = parentMenuWithSubmenus?.subMenuModels || []

  //   setMenuItems(submenuItems)
  // }, [userDetails?.id])

  useEffect(() => {
    let parentMenuWithSubmenus

    if (tab === 'general') {
      parentMenuWithSubmenus = userDetails?.id ? userDetails?.businessGeneralMenus?.generalMenu?.find((menu) => menu?.subMenuModels?.length > 0) : freeMenusToDisplay?.find((menu) => menu?.subMenuModels?.length > 0)
    } else {
      parentMenuWithSubmenus = userDetails?.id ? userDetails?.businessGeneralMenus?.businessMenu?.find((menu) => menu?.subMenuModels?.length > 0) : freeMenusToDisplay?.find((menu) => menu?.subMenuModels?.length > 0)
    }

    const submenuItems = parentMenuWithSubmenus?.subMenuModels || []

    // Prevent unnecessary updates to state if submenuItems hasn't changed
    if (JSON.stringify(submenuItems) !== JSON.stringify(menuItems)) {
      setMenuItems(submenuItems)
    }
  }, [userDetails?.id, freeMenusToDisplay, location.search]) // Added menuItems to dependency array for comparison

  // useEffect to set coffeewebParentItems (items with coffeeweb as parentId - for right content only)
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const currentTab = params.get('tab')

    let allMenuItems = []

    if (currentTab === 'general') {
      allMenuItems = userDetails?.id ? userDetails?.businessGeneralMenus?.generalMenu || [] : freeMenusToDisplay || []
    } else {
      allMenuItems = userDetails?.id ? userDetails?.businessGeneralMenus?.businessMenu || [] : freeMenusToDisplay || []
    }

    // Find coffeeweb menu item to get its id
    const coffeewebMenu = allMenuItems.find((menu) => {
      const normalizedUrl = normalizeUrl(menu.menuUrl)

      return normalizedUrl === 'coffeeweb' || normalizedUrl === 'coffeewebai'
    })

    // Get menu items from main menu that have coffeeweb's id as parentId
    const itemsWithCoffeewebParent = coffeewebMenu?.id ? allMenuItems.filter((menu) => menu.parentId === coffeewebMenu.id) : []

    // Prevent unnecessary updates to state if items haven't changed
    if (JSON.stringify(itemsWithCoffeewebParent) !== JSON.stringify(coffeewebParentItems)) {
      setCoffeewebParentItems(itemsWithCoffeewebParent)
    }
  }, [userDetails?.id, location.search])

  useEffect(() => {
    getGeolocation()
  }, [])

  useEffect(() => {
    if (typeof realtimeSubscriptionData?.IsTrialUser === 'boolean') {
      setIsTrialUser(realtimeSubscriptionData.IsTrialUser)
    }
  }, [realtimeSubscriptionData?.IsTrialUser])

  const rawComponentMap = {
    support: SupportRequest,
    'user-guide': UserGuide,
    coffeecart: CoffeeCart,
    coffeecast: CoffeeCast,
    'coffee-games': CoffeeGames,
    'coffee-community': CoffeeCommunities,
    'about-coffeeweb': AboutUs, // Note: camelCase or PascalCase — doesn't matter
    'coffee-post': CoffeePostScreen,
    cafes: CoffeeCafes
  }

  const componentMap = Object.entries(rawComponentMap).reduce((acc, [key, value]) => {
    acc[key.toLowerCase()] = value

    return acc
  }, {})

  const rightContent = () => {
    let activeItem = menuItems.find((el) => normalizeUrl(el.menuUrl) === active)

    // If not found, check in coffeewebParentItems (items with coffeeweb as parent)
    if (!activeItem) {
      activeItem = coffeewebParentItems.find((el) => normalizeUrl(el.menuUrl) === active)
    }

    if (!activeItem && normalizeUrl(active) === normalizeUrl('about-coffeeweb')) {
      activeItem = {
        menuName: 'About CoffeeWeb',
        menuUrl: 'about-coffeeweb'
      }
    } else if (!activeItem && normalizeUrl(active) === normalizeUrl(ROUTE_STRINGS.coffeePost)) {
      activeItem = {
        menuName: 'Coffee Post',
        menuUrl: ROUTE_STRINGS.coffeePost
      }
    }

    if (!activeItem) return null

    const Component = componentMap[normalizeUrl(activeItem.menuUrl)]

    if (!Component) return <div className="coming-soon-component">{activeItem.menuUrl} Coming Soon</div>

    const hasHeader = hideViews?.includes(view)

    return <Component componentLabel={activeItem.menuName} setActive={setActive} normalizeUrl={normalizeUrl} locationRef={locationRef} setShowAboutUsNavLogo={setShowAboutUsNavLogo} showModal={showModal} setShowModal={setShowModal} searchTextField={searchTextField} containerRef={containerRef} hasHeader={hasHeader} onOrganizationStatusChange={setOrganizationStatus} />
  }

  function generateLinearGradientColor() {
    const defaultColor = '#50b3f6'
    const color = userDetails?.id ? realtimeSubscriptionData?.subscriptionDetails?.SubscriptionColor || userDetails?.sub?.subscriptionColor || '#50b3f6, #8DD5F4, #50b3f6' : '#ae5e49, #d89a8b, #ae5e49'

    const colorPart1 = color.slice(0, 7)
    const colorPart2 = color.slice(9, 16)
    const colorPart3 = color.slice(18, 27)

    return `linear-gradient(100deg, ${colorPart1}, ${colorPart2}, ${colorPart3})`
  }

  const validityFormatter = (date) => {
    const [validityString, validTill] = date.split(':').map((el) => el.trim())

    return `${validityString}: ${dateFormatBasedOnUser(validTill, 'spaceFormat')}`
  }

  const navigateToSubscription = (event) => {
    event.stopPropagation()
    history.push(ROUTE_STRINGS.subscription)
  }

  const handleMenuClick = (menuUrl) => {
    const cleanUrl = normalizeUrl(menuUrl)

    setActive(cleanUrl)

    const newParams = new URLSearchParams(location.search)

    newParams.set('view', cleanUrl)
    history.push(`${location.pathname}?${newParams.toString()}`)
  }

  const getGeolocation = async () => {
    try {
      if (!userDetails) {
        const countryList = await apiAdapterCoffeeWeb.getAllCountries()
        const locationResponse = await commonMethods.searchLocation()

        if (locationResponse && countryList?.data?.length > 0) {
          const matchedCountry = countryList?.data?.find((c) => {
            const countryName = locationResponse.country.toLowerCase()

            return c?.nicename?.toLowerCase() === countryName || c?.name?.toLowerCase() === countryName
          })

          const result = matchedCountry ? { nicename: matchedCountry.nicename || matchedCountry.name, flagsUrl: matchedCountry.flagsUrl } : null

          setUserGeolocation({ ...result, city: locationResponse?.name || '', region: locationResponse?.region || '' })
        }
      }
    } catch {
      setUserGeolocation(null)
    }
  }

  const handleLogin = () => {
    if (userLoginStatus === false) {
      history.push(ROUTE_STRINGS.login)
    } else {
      history.push(ROUTE_STRINGS.createAccount)
    }
  }

  const openPostModal = () => {
    setShowModal(true)
  }

  const AvatarCard = () => (
    <div className="avatar-container">
      <div
        className={`avatar-card ${userDetails?.id ? '' : 'guest-user-avatar-card'}`}
        onClick={() => {
          // setModalShow(true)
          // trackEvent(USER_EVENTS.PROFILE_SCREEN) // user-analytics
        }}
        style={{
          '--custom-background': generateLinearGradientColor(userDetails, realtimeSubscriptionData)
        }}
        data-testid="avatar-card"
      >
        <div className="avatarDiv" data-testid="avatar-div-container">
          <div>
            <img alt="" className={`avatarUserCountryImg ${userDetails?.id ? '' : 'guest'}`} src={userDetails?.id ? userDetails?.countryFlag : userGeolocation?.flagsUrl || 'https://coffeeweb.s3.ap-south-1.amazonaws.com/CoffeeWebLogoWhite.png'} title="avatar-country-flag" />
          </div>
          <div className="avatar-user-personal-details" data-testid="user-personal-details-container">
            <div className="username-edit-button" data-testid="user-name-container">
              <div>
                {userDetails?.id ? (
                  <>
                    {userDetails?.firstName} {userDetails?.lastName}
                  </>
                ) : (
                  <>Guest User</>
                )}
              </div>
              {/* Commented below pencil icon for edit profile as per discussion */}
              {/* <div>
                <i className="pi pi-pencil" data-testid="pencil-icon-container" />
              </div> */}
            </div>
            {!userDetails?.id && (
              <div className="user-location" data-testid="user-location-container">
                {userGeolocation?.city && `${userGeolocation?.city}, `}
                {userGeolocation?.nicename && ` ${userGeolocation?.nicename}`}
              </div>
            )}
            {userDetails?.occupationName !== 'NA' && (
              <div className="user-occupation" data-testid="user-occupation-container">
                {userDetails?.occupationName}
              </div>
            )}
          </div>
        </div>

        {userDetails?.phone && (
          <div className="user-field" data-testid="user-phone-container">
            <span className="label">{t('PHONE')} : + </span>
            <span className="value">
              {userDetails?.countryPhoneCode} {userDetails?.phone}
            </span>
          </div>
        )}

        {userDetails?.email && (
          <div className="user-field" data-testid="user-email-container">
            <span className="label">{t('EMAIL')} : </span>
            <span className="value">{userDetails?.email}</span>
          </div>
        )}

        <div className={`userSubscriptionDetails ${userDetails ? 'login-user' : ''}`} data-testid="user-subscription-details-container">
          <div className="subscriptionDate" data-testid="subscription-date-and-valid-days-container">
            {userDetails?.sub?.endDate && userDetails?.sub?.subType !== 1 && <div data-testid="subscription-valid-days-container"> {userDetails?.sub?.subscriptionValidTill}</div>}
            {userDetails?.sub?.endDate && userDetails?.sub?.subType !== 1 && <div data-testid="subscription-valid-date-container"> {validityFormatter(userDetails?.sub?.subscriptionValidTillDate, dateFormatBasedOnUser)}</div>}
          </div>
          <div className="subscriptionType" data-testid="user-subscription-name">
            {displayedSubscriptionName}
          </div>
        </div>
        {!userDetails?.id && (
          <div className="user-location" style={{ width: '100%', display: 'flex', justifyContent: 'space-between', marginTop: '16px' }} data-testid="user-location-container">
            <div>
              {new Date().toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
              })}
            </div>
            <div>{new Date().toLocaleTimeString('en-GB', { hour12: true, hour: '2-digit', minute: '2-digit' })?.toUpperCase()}</div>
          </div>
        )}
        {shouldShowRenewAction && (
          <div className="upgrade-button-large-screen-container" data-testid="upgrade-button-container">
            {appSettings?.showrenewal && (
              <button className="upgradeButtonLargeScreen" data-testid="upgrade-action-container" onClick={navigateToSubscription}>
                {displayedRenewActionText}
              </button>
            )}
          </div>
        )}
        {!userDetails?.id && (
          <div className="upgrade-button-large-screen-container" data-testid="upgrade-button-container">
            <button className="upgradeButtonLargeScreen" data-testid="upgrade-action-container" onClick={handleLogin}>
              {t('LOGIN_AND_CREATE_ACCOUNT')}
            </button>
          </div>
        )}
      </div>
    </div>
  )

  // const currentPath = userDetails?.id ? location.pathname.split('/')[2] : location.pathname.split('/')[1] // commented if we need to use anywhere

  const scrollToTop = () => {
    containerRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <>
      {hideViews?.includes(view) && (
        <div className="search-header-container">
          <div className="search-section" ref={containerRef}>
            <div className="home-icon-wrapper" onClick={scrollToTop}>
              <i className="pi pi-home search-home-icon"></i>
            </div>

            <div className="search-input-wrapper">
              <span className="p-input-icon-left search-input-container">
                <i className="pi pi-search search-input-icon" />
                <InputText value={searchTextField} onChange={(e) => setSearchTextField(e.target.value)} placeholder={t('SEARCH')} className="search-input-field" />
              </span>
            </div>
          </div>
        </div>
      )}
      <div className="coffeeweb-page-container">
        <div className={`coffeeweb-page coffeeweb-theme ${userDetails?.id ? '' : 'guest-user'} ${hideViews?.includes(view) ? 'with-header' : ''}`}>
          <aside className={`left-panel ${userDetails?.id ? '' : 'guest-user-left-panel'} ${hideViews?.includes(view) ? 'with-header' : ''} ${view === 'coffee-post' ? 'coffee-post-module-left-panel' : ''}`}>
            <AvatarCard />
            <div className="menu-items-container">
              <div className="menu">
                {menuItems.map((m) => (
                  <div
                    key={m.key}
                    className={`menu-items ${normalizeUrl(m.menuUrl) === active ? 'active' : ''}`}
                    onClick={() => {
                      const normalizedUrl = normalizeUrl(m.menuUrl)

                      handleMenuClick(m.menuUrl)
                      setShowAboutUsNavLogo(true)
                      locationRef.current = normalizedUrl
                      previousViewRef.current = normalizedUrl
                    }}
                  >
                    <img style={{ width: '34px' }} src={m?.menuIcon} alt={m.menuUrl} />
                    <span style={{ marginLeft: '8px', fontSize: '20px' }}>{m?.menuName}</span>
                  </div>
                ))}
              </div>
            </div>
            {hideViews?.includes(view) && userDetails && (
              <div className="coffee-post-actions">
                <Button buttonType={'CUSTOM'} buttonLabel={organizationStatus === 2 ? t('PENDING_FOR_APPROVAL') || 'Pending for approval' : t('CREATE_POST')} variant={'hard-action'} onButtonClick={openPostModal} buttonIcon={organizationStatus === 2 ? null : <i className="pi pi-plus upload-icon" />} />
              </div>
            )}
          </aside>

          <main className={`right-panel ${userDetails?.id ? '' : 'guest-user'} ${hideViews?.includes(view) ? 'with-header' : ''} ${view === 'coffee-post' ? 'coffee-post-module-right-panel' : ''}`}>
            <div className="right-content">{rightContent()}</div>
          </main>
        </div>
        {showAboutUsNavLogo && (
          <img
            src="https://coffeeweb.s3.ap-south-1.amazonaws.com/Dev-AboutCoffeeWeb.png"
            alt=""
            className="about-coffee-nav"
            onClick={() => {
              // Store current active view as previous before navigating to about-coffeeweb
              const prevView = active && active !== 'about-coffeeweb' ? active : previousViewRef.current || (menuItems.length > 0 ? normalizeUrl(menuItems[0]?.menuUrl) : '')

              if (prevView) {
                previousViewRef.current = prevView
                locationRef.current = prevView
                // Navigate with prev parameter to preserve it on refresh
                // The useEffect will handle setting the active state
                const newParams = new URLSearchParams(location.search)

                newParams.set('view', 'about-coffeeweb')
                newParams.set('prev', prevView)
                history.push(`${location.pathname}?${newParams.toString()}`)
              } else {
                handleMenuClick('about-coffeeWeb')
              }
              setShowAboutUsNavLogo(false)
            }}
          />
        )}
      </div>
    </>
  )
}

export default CoffeeWebSocial
